import React, { useState, useEffect } from "react";
import { Calendar, Clock, Repeat, Bell, Save, X, Plus, Info, Trash2 } from "lucide-react";
import { motion } from "framer-motion";
import { useOrganization } from '../../../context/OrganizationContext';
import { useUser } from '../../../context/UserContext';
import { createSchedule, getSchedulesByThreadId, deleteSchedule } from '../../../services/scheduleService';

// Helper function to convert user-friendly config to cron expression
const generateCronExpression = (config) => {
  const { frequency, time, daysOfWeek, dayOfMonth } = config;
  const [hour, minute] = time.split(':');
  
  switch (frequency) {
    case 'daily':
      return `${minute} ${hour} * * *`;
    case 'weekdays':
      return `${minute} ${hour} * * 1-5`;
    case 'weekly':
      if (daysOfWeek.length === 0) return `${minute} ${hour} * * 1`;
      // Convert day names to numbers (0=Sun, 1=Mon, etc.)
      const dayMap = { 'Sun': 0, 'Mon': 1, 'Tue': 2, 'Wed': 3, 'Thu': 4, 'Fri': 5, 'Sat': 6 };
      const dayNumbers = daysOfWeek.map(d => dayMap[d]).sort().join(',');
      return `${minute} ${hour} * * ${dayNumbers}`;
    case 'monthly':
      return `${minute} ${hour} ${dayOfMonth} * *`;
    default:
      return `${minute} ${hour} * * *`;
  }
};

// Helper function to get human-readable description
const getScheduleDescription = (config) => {
  const { frequency, time, daysOfWeek, dayOfMonth } = config;
  
  switch (frequency) {
    case 'daily':
      return `Daily at ${time}`;
    case 'weekdays':
      return `Every weekday (Mon-Fri) at ${time}`;
    case 'weekly':
      if (daysOfWeek.length === 0) return `Every Monday at ${time}`;
      return `Every ${daysOfWeek.join(', ')} at ${time}`;
    case 'monthly':
      return `Monthly on day ${dayOfMonth} at ${time}`;
    default:
      return `At ${time}`;
  }
};

const Schedule = ({ thread, threads1, setThreads1, onBack }) => {
  const { organizationData, orgId } = useOrganization();
  const { userInfo } = useUser();
  
  // User-friendly schedule configuration state
  const [scheduleConfig, setScheduleConfig] = useState({
    frequency: 'daily', // 'daily', 'weekdays', 'weekly', 'monthly'
    time: '09:00',
    daysOfWeek: [], // For weekly schedules: ['Mon', 'Tue', etc.]
    dayOfMonth: 1, // For monthly schedules
    overlapPolicy: 'BUFFER_ONE', // Server default
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone // User's browser timezone
  });

  const [isSaving, setIsSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [existingSchedules, setExistingSchedules] = useState([]);
  const [isLoadingSchedules, setIsLoadingSchedules] = useState(false); // Start with false
  const [isDeleting, setIsDeleting] = useState(false);

  // Reset state when thread changes
  useEffect(() => {
    setExistingSchedules([]);
    setIsLoadingSchedules(false);
    setSaveError(null);
  }, [thread?._id]);

  // Load existing schedules for this thread
  useEffect(() => {
    const fetchSchedules = async () => {
      // Check if we have the required data
      if (!thread?._id || !orgId) {
        setIsLoadingSchedules(false); // Set to false if data is not available
        return;
      }
      
      setIsLoadingSchedules(true);
      
      try {
        const response = await getSchedulesByThreadId(orgId, thread._id);
        const schedules = response?.data?.data || [];
        setExistingSchedules(schedules);
        
        // If schedule exists, pre-populate the form (optional)
        if (schedules.length > 0) {
          const schedule = schedules[0]; // Use the first active schedule
          // Would need to parse cron back to UI format for editing
          setScheduleConfig(prev => ({
            ...prev,
            overlapPolicy: schedule.overlap_policy || 'BUFFER_ONE'
          }));
        }
      } catch (error) {
        setExistingSchedules([]); // Reset to empty on error
      } finally {
        setIsLoadingSchedules(false);
      }
    };

    fetchSchedules();
  }, [thread?._id, orgId]);

  // Refetch schedules function
  const refetchSchedules = async () => {
    if (!thread?._id || !orgId) return;
    
    setIsLoadingSchedules(true);
    
    try {
      const response = await getSchedulesByThreadId(orgId, thread._id);
      const schedules = response?.data?.data || [];
      setExistingSchedules(schedules);
    } catch (error) {
    } finally {
      setIsLoadingSchedules(false);
    }
  };

  const handleFrequencyChange = (frequency) => {
    setScheduleConfig(prev => ({
      ...prev,
      frequency
    }));
  };

  const handleTimeChange = (time) => {
    setScheduleConfig(prev => ({
      ...prev,
      time
    }));
  };

  const handleDayToggle = (day) => {
    setScheduleConfig(prev => ({
      ...prev,
      daysOfWeek: prev.daysOfWeek.includes(day)
        ? prev.daysOfWeek.filter(d => d !== day)
        : [...prev.daysOfWeek, day]
    }));
  };

  const handleSaveSchedule = async () => {
    setIsSaving(true);
    setSaveError(null);
    
    try {
      // Validate that thread has MongoDB _id
      if (!thread._id) {
        setSaveError('Cannot schedule: Thread ID is missing.');
        setIsSaving(false);
        return;
      }
      
      // Convert user-friendly config to server payload
      const cronExpression = generateCronExpression(scheduleConfig);
      const description = getScheduleDescription(scheduleConfig); // Always use auto-generated description
      
      const payload = {
        threadId: thread._id, // Use MongoDB _id as the thread identifier
        cronExpression: cronExpression,
        description: description,
        overlapPolicy: scheduleConfig.overlapPolicy,
        timezone: scheduleConfig.timezone // Include user's timezone
      };

      // Call the API
      const response = await createSchedule(orgId, payload);
      
      // Refetch schedules to update UI
      await refetchSchedules();

      // Update threads1 state to trigger any parent component updates
      if (setThreads1 && response.data?.data) {
        const scheduleData = response.data.data;
        setThreads1(prev => prev.map(t => 
          t._id === thread._id
            ? { 
                ...t, 
                schedule: scheduleData
              }
            : t
        ));
      }

      setSaveSuccess(true);
      // Don't auto-navigate away - let user see the success state
      setTimeout(() => {
        setSaveSuccess(false);
      }, 3000);
      
    } catch (error) {
      setSaveError(error.response?.data?.message || error.message || 'Failed to save schedule');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteSchedule = async (scheduleId) => {
    if (!window.confirm('Are you sure you want to delete this schedule?')) {
      return;
    }

    setIsDeleting(true);
    setSaveError(null);
    
    try {
      await deleteSchedule(orgId, scheduleId);
      
      // Refetch schedules to update UI
      await refetchSchedules();
      
      // Update threads1 state to remove schedule
      if (setThreads1) {
        setThreads1(prev => prev.map(t => 
          t._id === thread._id
            ? { ...t, schedule: null }
            : t
        ));
      }
      
    } catch (error) {
      setSaveError(error.response?.data?.message || error.message || 'Failed to delete schedule');
    } finally {
      setIsDeleting(false);
    }
  };

  const daysOfWeek = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

  return (
    <div className="flex h-full flex-col bg-background">
      {/* Header */}
      <div className="border-b border-border bg-background/80 backdrop-blur-sm px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-gradient-to-br from-purple-500 to-blue-500">
              <Calendar className="h-5 w-5 text-white" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-foreground">Schedule Analysis</h2>
              <p className="text-sm text-grey">Automate recurring GA4 reports</p>
            </div>
          </div>
        </div>
        
        {/* Error Message */}
        {saveError && (
          <div className="mt-4 px-6 py-3 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-sm text-red-600">⚠️ {saveError}</p>
          </div>
        )}
      </div>

      {/* Main Content */}
      <div className="flex-1 overflow-y-auto p-6">
        <div className="mx-auto max-w-4xl space-y-6">
          
          {/* Loading State */}
          {isLoadingSchedules ? (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="rounded-xl border border-border bg-white p-6 shadow-sm"
            >
              <div className="flex items-center justify-center gap-2 text-grey">
                <div className="h-4 w-4 animate-spin rounded-full border-2 border-grey border-t-transparent" />
                <span className="text-sm">Loading schedules...</span>
              </div>
            </motion.div>
          ) : existingSchedules.length > 0 ? (
            // Show existing schedule only (no creation form)
            <>
              {/* Run Details */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3 }}
                className="rounded-xl border border-border bg-white p-6 shadow-sm"
              >
                <h3 className="mb-4 text-base font-semibold text-foreground">Run Details</h3>
                <div className="grid gap-2.5 text-sm">
                  <div className="flex items-start gap-3">
                    <span className="text-grey min-w-[90px]">Prompt:</span>
                    <span className="text-foreground flex-1">{thread?.prompt || 'N/A'}</span>
                  </div>
                  <div className="flex items-start gap-3">
                    <span className="text-grey min-w-[90px]">Agent Type:</span>
                    <span className="text-foreground capitalize">{thread?.agentType || 'N/A'}</span>
                  </div>
                  <div className="flex items-start gap-3">
                    <span className="text-grey min-w-[90px]">Period Type:</span>
                    <span className="text-foreground capitalize">{thread?.periodType || 'N/A'}</span>
                  </div>
                  {thread?.periodType === 'rolling' && thread?.config?.rolling?.days && (
                    <div className="flex items-start gap-3">
                      <span className="text-grey min-w-[90px]">Rolling Period:</span>
                      <span className="text-foreground">Last {thread.config.rolling.days} days</span>
                    </div>
                  )}
                  <div className="flex items-start gap-3">
                    <span className="text-grey min-w-[90px]">Granularity:</span>
                    <span className="text-foreground capitalize">{thread?.granularity || 'N/A'}</span>
                  </div>
                  {thread?.config?.comparison?.enabled && (
                    <div className="flex items-start gap-3">
                      <span className="text-grey min-w-[90px]">Comparison:</span>
                      <span className="text-foreground capitalize text-xs">
                        {thread.config.comparison.type === 'sameLastYear' ? 'Same period last year' : 'Custom period'}
                      </span>
                    </div>
                  )}
                  {thread?.started_at && (
                    <div className="flex items-start gap-3">
                      <span className="text-grey min-w-[90px]">Created:</span>
                      <span className="text-foreground text-xs">
                        {new Date(thread.started_at).toLocaleString()}
                      </span>
                    </div>
                  )}
                </div>
              </motion.div>

              {/* Existing Schedule */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: 0.1 }}
                className="rounded-xl border-2 border-green-200 bg-green-50 p-6 shadow-sm"
              >
                <h3 className="mb-4 flex items-center gap-2 text-base font-semibold text-foreground">
                  <Calendar className="h-5 w-5 text-green-600" />
                  Active Schedule
                </h3>
                {existingSchedules.map((schedule) => {
                  return (
                    <div key={schedule._id} className="rounded-lg bg-white p-4 border border-green-200">
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1 space-y-2">
                          <div className="flex items-center gap-2">
                            <Clock className="h-4 w-4 text-brand-blue-dark flex-shrink-0" />
                            <p className="text-sm font-medium text-foreground">
                              {schedule.description || schedule.payload?.description || schedule.cron_expression || 'Scheduled Analysis'}
                            </p>
                          </div>
                          <div className="grid gap-1.5 text-xs text-grey pl-6">
                            <div>
                              <span className="font-medium">Status:</span>{' '}
                              <span className={`capitalize ${schedule.status === 'active' ? 'text-green-600' : 'text-grey'}`}>
                                {schedule.status}
                              </span>
                            </div>
                            {schedule.timezone && (
                              <div>
                                <span className="font-medium">Timezone:</span>{' '}
                                {schedule.timezone}
                              </div>
                            )}
                            <div>
                              <span className="font-medium">Created:</span>{' '}
                              {new Date(schedule.created_at).toLocaleString()}
                            </div>
                          </div>
                        </div>
                        <button
                          onClick={() => handleDeleteSchedule(schedule._id)}
                          disabled={isDeleting}
                          className="flex items-center gap-1.5 rounded-lg px-3 py-2 text-sm font-medium text-red-600 hover:bg-red-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                          title="Delete schedule"
                        >
                          <Trash2 className="h-4 w-4" />
                          Delete
                        </button>
                      </div>
                    </div>
                  );
                })}
                <p className="mt-4 text-xs text-grey">
                  To create a new schedule, delete the existing one first.
                </p>
              </motion.div>

              {/* Back Button */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: 0.2 }}
                className="flex items-center justify-start pt-2"
              >
                <button
                  onClick={onBack}
                  className="rounded-lg px-4 py-2 text-sm font-medium text-grey hover:bg-gray-100 transition-colors"
                >
                  Back to Analysis
                </button>
              </motion.div>
            </>
          ) : (
            // Show creation form only (no existing schedule)
            <>
              {/* Run Details */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3 }}
                className="rounded-xl border border-border bg-white p-6 shadow-sm"
              >
                <h3 className="mb-4 text-base font-semibold text-foreground">Run Details</h3>
                <div className="grid gap-2.5 text-sm">
                  <div className="flex items-start gap-3">
                    <span className="text-grey min-w-[90px]">Prompt:</span>
                    <span className="text-foreground flex-1">{thread?.prompt || 'N/A'}</span>
                  </div>
                  <div className="flex items-start gap-3">
                    <span className="text-grey min-w-[90px]">Agent Type:</span>
                    <span className="text-foreground capitalize">{thread?.agentType || 'N/A'}</span>
                  </div>
                  <div className="flex items-start gap-3">
                    <span className="text-grey min-w-[90px]">Period Type:</span>
                    <span className="text-foreground capitalize">{thread?.periodType || 'N/A'}</span>
                  </div>
                  {thread?.periodType === 'rolling' && thread?.config?.rolling?.days && (
                    <div className="flex items-start gap-3">
                      <span className="text-grey min-w-[90px]">Rolling Period:</span>
                      <span className="text-foreground">Last {thread.config.rolling.days} days</span>
                    </div>
                  )}
                  <div className="flex items-start gap-3">
                    <span className="text-grey min-w-[90px]">Granularity:</span>
                    <span className="text-foreground capitalize">{thread?.granularity || 'N/A'}</span>
                  </div>
                  {thread?.config?.comparison?.enabled && (
                    <div className="flex items-start gap-3">
                      <span className="text-grey min-w-[90px]">Comparison:</span>
                      <span className="text-foreground capitalize text-xs">
                        {thread.config.comparison.type === 'sameLastYear' ? 'Same period last year' : 'Custom period'}
                      </span>
                    </div>
                  )}
                  {thread?.started_at && (
                    <div className="flex items-start gap-3">
                      <span className="text-grey min-w-[90px]">Created:</span>
                      <span className="text-foreground text-xs">
                        {new Date(thread.started_at).toLocaleString()}
                      </span>
                    </div>
                  )}
                </div>
              </motion.div>

              {/* Frequency Selection */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: 0.1 }}
                className="rounded-xl border border-border bg-white p-6 shadow-sm"
              >
                <h3 className="mb-4 flex items-center gap-2 text-base font-semibold text-foreground">
                  <Repeat className="h-5 w-5 text-brand-blue-dark" />
                  How often should this run?
                </h3>
                <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                  {[
                    { value: 'daily', label: 'Daily', desc: 'Every day' },
                    { value: 'weekdays', label: 'Weekdays', desc: 'Mon-Fri' },
                    { value: 'weekly', label: 'Weekly', desc: 'Select days' },
                    { value: 'monthly', label: 'Monthly', desc: 'Pick a day' }
                  ].map((freq) => (
                    <button
                      key={freq.value}
                      type="button"
                      onClick={() => handleFrequencyChange(freq.value)}
                      className={`rounded-lg border-2 px-4 py-3 text-sm font-medium transition-all text-left ${
                        scheduleConfig.frequency === freq.value
                          ? 'border-brand-pink bg-brand-pink/10 text-brand-pink'
                          : 'border-border bg-background text-grey hover:border-brand-blue-light'
                      }`}
                    >
                      <div className="font-semibold">{freq.label}</div>
                      <div className="text-xs opacity-75 mt-0.5">{freq.desc}</div>
                    </button>
                  ))}
                </div>

                {/* Weekly - Day Selection */}
                {scheduleConfig.frequency === 'weekly' && (
                  <div className="mt-4">
                    <p className="mb-3 text-sm font-medium text-grey">Which days?</p>
                    <div className="flex flex-wrap gap-2">
                      {daysOfWeek.map((day) => (
                        <button
                          key={day}
                          type="button"
                          onClick={() => handleDayToggle(day)}
                          className={`rounded-lg px-4 py-2 text-sm font-medium transition-all ${
                            scheduleConfig.daysOfWeek.includes(day)
                              ? 'bg-brand-pink text-white'
                              : 'bg-gray-100 text-grey hover:bg-gray-200'
                          }`}
                        >
                          {day}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Monthly - Day Selection */}
                {scheduleConfig.frequency === 'monthly' && (
                  <div className="mt-4">
                    <label className="mb-2 block text-sm font-medium text-grey">Which day of the month?</label>
                    <select
                      value={scheduleConfig.dayOfMonth}
                      onChange={(e) => setScheduleConfig(prev => ({ ...prev, dayOfMonth: parseInt(e.target.value) }))}
                      className="w-full rounded-lg border border-border px-3 py-2 text-sm focus:border-brand-pink focus:outline-none focus:ring-2 focus:ring-brand-pink/20"
                    >
                      {Array.from({ length: 31 }, (_, i) => i + 1).map(day => (
                        <option key={day} value={day}>
                          {day}{day === 1 ? 'st' : day === 2 ? 'nd' : day === 3 ? 'rd' : 'th'} of the month
                        </option>
                      ))}
                    </select>
                  </div>
                )}
              </motion.div>

              {/* Time Selection */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: 0.3 }}
                className="rounded-xl border border-border bg-white p-6 shadow-sm"
              >
                <h3 className="mb-4 flex items-center gap-2 text-base font-semibold text-foreground">
                  <Clock className="h-5 w-5 text-brand-blue-dark" />
                  What time?
                </h3>
                <div>
                  <label className="mb-2 block text-sm font-medium text-grey">Time</label>
                  <input
                    type="time"
                    value={scheduleConfig.time}
                    onChange={(e) => handleTimeChange(e.target.value)}
                    className="w-full rounded-lg border border-border px-3 py-2 text-sm focus:border-brand-pink focus:outline-none focus:ring-2 focus:ring-brand-pink/20"
                  />
                  <div className="mt-3 rounded-lg bg-blue-50 border border-blue-200 p-3">
                    <div className="flex items-start gap-2">
                      <Info className="h-4 w-4 text-blue-600 flex-shrink-0 mt-0.5" />
                      <div className="text-xs text-blue-900">
                        <p className="font-medium mb-1">Timezone: {scheduleConfig.timezone}</p>
                        <p className="text-blue-700">
                          The schedule will run at this time in your current timezone, regardless of where you travel or access the system from.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>

              {/* Schedule Summary */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: 0.4 }}
                className="rounded-xl border-2 border-brand-blue-light bg-gradient-to-br from-brand-blue-light/5 to-brand-pink/5 p-6 shadow-sm"
              >
                <h3 className="mb-4 flex items-center gap-2 text-base font-semibold text-foreground">
                  <Repeat className="h-5 w-5 text-brand-pink" />
                  Schedule Summary
                </h3>
                <div className="rounded-lg bg-white p-4 space-y-2">
                  <div className="flex items-start gap-3">
                    <Clock className="h-4 w-4 text-brand-blue-dark mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="text-sm font-medium text-foreground">
                        {getScheduleDescription(scheduleConfig)}
                      </p>
                      <p className="text-xs text-grey mt-1">
                        Timezone: {scheduleConfig.timezone}
                      </p>
                    </div>
                  </div>
                  <div className="pt-2 border-t border-border">
                    <p className="text-xs text-grey">
                      Next run will be calculated after schedule is saved
                    </p>
                  </div>
                </div>
              </motion.div>

              {/* Action Buttons */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: 0.5 }}
                className="flex items-center justify-between pt-2"
              >
                <button
                  onClick={onBack}
                  className="rounded-lg px-4 py-2 text-sm font-medium text-grey hover:bg-gray-100 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveSchedule}
                  disabled={isSaving}
                  className="flex items-center gap-2 rounded-lg bg-gradient-to-r from-brand-pink to-brand-blue-dark px-6 py-2 text-sm font-medium text-white hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                >
                  <Save className="h-4 w-4" />
                  {isSaving ? 'Saving...' : 'Save Schedule'}
                </button>
              </motion.div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default Schedule;
